/** UAE and Saudi Arabia national numbers are typically 9–10 digits. */
const LONG_PHONE_COUNTRY_CODES = new Set(["971", "966"]);

/** Default GCC countries (Bahrain, Kuwait, Oman, Qatar, etc.): 8–9 digits. */
const DEFAULT_PHONE_LENGTH = { min: 8, max: 10 };
const LONG_PHONE_LENGTH = { min: 9, max: 10 };

export type PhoneLengthRule = { min: number; max: number };

export function getPhoneLengthRule(countryCode?: string | null): PhoneLengthRule {
  if (countryCode && LONG_PHONE_COUNTRY_CODES.has(countryCode)) {
    return LONG_PHONE_LENGTH;
  }
  return DEFAULT_PHONE_LENGTH;
}

export function getPhoneMaxLength(countryCode?: string | null): number {
  return getPhoneLengthRule(countryCode).max;
}

export function isValidPhoneLength(phone: string | undefined | null, countryCode?: string | null): boolean {
  const length = phone?.length || 0;
  const { min, max } = getPhoneLengthRule(countryCode);
  return length >= min && length <= max;
}

/** i18n key for country-specific min/max phone length messages. */
export function getPhoneLengthErrorKey(countryCode?: string | null): "phoneLength9or10" | "phoneLength8or9" {
  const { min, max } = getPhoneLengthRule(countryCode);
  if (min === 9 && max === 10) return "phoneLength9or10";
  return "phoneLength8or9";
}

export function sanitizePhoneInput(value: string, countryCode?: string | null): string {
  const digitsOnly = value.replace(/[^0-9]/g, "");
  return digitsOnly.slice(0, getPhoneMaxLength(countryCode));
}

export function isFirebaseInvalidPhoneError(error: unknown): boolean {
  const code = getFirebaseErrorCode(error);
  const message = getFirebaseErrorMessage(error);
  return (
    code === "auth/invalid-phone-number" ||
    /auth\/invalid-phone-number|TOO_LONG|TOO_SHORT/i.test(message)
  );
}

function getFirebaseErrorCode(error: unknown): string {
  if (typeof error === "object" && error !== null && "code" in error) {
    return String((error as { code?: string }).code || "");
  }
  return "";
}

function getFirebaseErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error ?? "");
}

/** True when the error message is a raw Firebase/auth string that should not be shown to users. */
export function isRawFirebaseAuthMessage(message: string): boolean {
  return /Firebase\s*:|auth\/|error-code\s*:?\s*-?\d+/i.test(message);
}

/**
 * User-facing toast for send/resend OTP failures.
 * Never surfaces raw Firebase messages like "Firebase: Error (auth/error-code:-39)."
 */
export function getSendPhoneOtpToastMessage(
  error: unknown,
  t: (key: string) => string,
  options?: { isResend?: boolean }
): string {
  if (isFirebaseInvalidPhoneError(error)) {
    return t("validation.mobileValid");
  }

  const code = getFirebaseErrorCode(error);
  const message = getFirebaseErrorMessage(error);

  if (
    code === "auth/too-many-requests" ||
    /too-many-requests/i.test(message)
  ) {
    return t("messages.otpTooManyRequests");
  }

  if (
    code === "auth/quota-exceeded" ||
    /quota-exceeded/i.test(message)
  ) {
    return t("messages.failedToSendOtp");
  }

  // Identity Toolkit backend errors (e.g. 503 "Error code: 39") and any other Firebase auth noise
  if (
    isRawFirebaseAuthMessage(message) ||
    /error-code\s*:?\s*-?\d+/i.test(code) ||
    code.startsWith("auth/")
  ) {
    return options?.isResend ? t("messages.failedToResendOtp") : t("messages.failedToSendOtp");
  }

  if (message && !isRawFirebaseAuthMessage(message)) {
    return message;
  }

  return options?.isResend ? t("messages.failedToResendOtp") : t("messages.failedToSendOtp");
}

